/**
 * pino-shaped structured logging for the browser: one JSON object per line on the console with
 * the CONVENTIONS fields (`service`, `tenant_id`, `agent_id`, `sid`, `cc_call_id`). Callers mask
 * numbers with `maskNumber` from contracts before logging them; DTMF digits are never logged.
 */
import type { LogLevelName } from './env.js';

export type LogFields = Record<string, unknown>;

export interface Logger {
  readonly level: LogLevelName;
  debug(fields: LogFields | string, msg?: string): void;
  info(fields: LogFields | string, msg?: string): void;
  warn(fields: LogFields | string, msg?: string): void;
  error(fields: LogFields | string, msg?: string): void;
  child(fields: LogFields): Logger;
}

const LEVEL_NO: Record<LogLevelName, number> = { debug: 20, info: 30, warn: 40, error: 50 };

export interface LoggerOptions {
  level: LogLevelName;
  service: 'phone' | 'dock' | 'sw';
  fields?: LogFields;
  sink?: (level: LogLevelName, line: string) => void;
  now?: () => number;
}

const consoleSink = (level: LogLevelName, line: string): void => {
  // eslint-disable-next-line no-console
  const fn = level === 'error' ? console.error : level === 'warn' ? console.warn : console.info;
  fn(line);
};

export function createLogger(opts: LoggerOptions): Logger {
  const sink = opts.sink ?? consoleSink;
  const now = opts.now ?? Date.now;
  const make = (bound: LogFields): Logger => {
    const emit = (level: LogLevelName, fields: LogFields | string, msg?: string): void => {
      if (LEVEL_NO[level] < LEVEL_NO[opts.level]) return;
      const f = typeof fields === 'string' ? {} : fields;
      const m = typeof fields === 'string' ? fields : msg;
      const rec: LogFields = { level: LEVEL_NO[level], time: now(), service: opts.service, ...bound, ...f };
      if (m !== undefined) rec['msg'] = m;
      let line: string;
      try {
        line = JSON.stringify(rec);
      } catch {
        line = JSON.stringify({ level: LEVEL_NO[level], time: now(), service: opts.service, msg: m, unserialisable: true });
      }
      sink(level, line);
    };
    return {
      level: opts.level,
      debug: (f, m) => emit('debug', f, m),
      info: (f, m) => emit('info', f, m),
      warn: (f, m) => emit('warn', f, m),
      error: (f, m) => emit('error', f, m),
      child: (fields) => make({ ...bound, ...fields }),
    };
  };
  return make(opts.fields ?? {});
}

export const silentLogger: Logger = createLogger({ level: 'error', service: 'phone', sink: () => undefined });
